/**
 * Iter 38a — Commercial Priority headline helpers (pure functions).
 *
 * Co-located with AnalysisResults.js so the Balanced/Consultancy
 * headline can render an opportunity-led label without the data
 * loop the Marketplace tabs use.
 *
 * Keeping these as pure exports lets a node-based test verify the
 * banding without bootstrapping React. The Risk pill stays a
 * separate, secondary reading — same thresholds as the legacy
 * `getRiskClass` (70/40) — so the operator can still read the risk
 * axis at a glance.
 */

export const COMMERCIAL_PRIORITY_LABELS = {
  engage_now:     'Engage now',
  worth_pursuing: 'Worth pursuing',
  strong_watch:   'Strong watch',
  watch:          'Watch',
  monitor_only:   'Monitor only',
};

export function commercialPriorityLabel(key) {
  return COMMERCIAL_PRIORITY_LABELS[key] || ((key || '').replace(/_/g, ' ').toUpperCase());
}

export function commercialPriorityClass(score) {
  if (typeof score !== 'number') return 'low';
  if (score >= 65) return 'high';
  if (score >= 35) return 'medium';
  return 'low';
}

export function riskPillClass(score) {
  if (typeof score !== 'number') return 'badge-type';
  if (score >= 70) return 'badge-high';
  if (score >= 40) return 'badge-medium';
  return 'badge-low';
}

export function riskPillLabel(score) {
  if (typeof score !== 'number') return 'Risk: —';
  if (score >= 70) return 'Risk: High';
  if (score >= 40) return 'Risk: Medium';
  return 'Risk: Low';
}

// Iter 38a — Headline selector. When the analysis payload carries
// `commercial_priority_score` (i.e. analysis re-scored under 38a or
// later), we headline Commercial Priority and reduce Risk to a pill.
// When it's absent (legacy saved analyses) we fall back to the
// legacy Overall Risk Score headline so old data renders safely.
export function selectHeadline(scores, recommendation, analyseScoreLabel) {
  const risk = (scores && typeof scores.overall_risk_score === 'number')
    ? scores.overall_risk_score
    : null;
  const cp = (scores && typeof scores.commercial_priority_score === 'number')
    ? scores.commercial_priority_score
    : null;
  const labelKey = (scores && scores.commercial_priority_label) || null;
  const reason = (scores && scores.commercial_priority_reason) || '';
  if (cp !== null) {
    return {
      mode: 'commercial_priority',
      score: cp,
      label: commercialPriorityLabel(labelKey),
      labelKey,
      reason,
      headlineLabelText: 'Commercial Priority / 100',
      headlineClass: commercialPriorityClass(cp),
      riskScore: risk,
    };
  }
  return {
    mode: 'legacy_risk',
    score: risk,
    label: (recommendation || '').replace(/_/g, ' ').toUpperCase(),
    labelKey: recommendation || null,
    reason: '',
    headlineLabelText: `${analyseScoreLabel || 'Overall Risk Score'} / 100`,
    headlineClass: (risk >= 70 ? 'high' : risk >= 40 ? 'medium' : 'low'),
    riskScore: risk,
  };
}
